import React from 'react';
import { FormikProps, FormikTouched } from 'formik';
import { showErrorToast } from 'src/utils';

export interface IUseWorkflowFormValidationResult<TValues> {
  /** False means the save must not run. */
  passesValidation: () => Promise<boolean>;
  fieldError: (field: keyof TValues) => string | undefined;
}

/**
 * @registry Gates a workflow form's save on validation and replays the server's field errors
 * onto Formik. The blocked-save toast names the failing fields through `fieldLabels` (falling
 * back to the field key) because the inputs may sit on a tab other than the one holding the footer.
 */
export function useWorkflowFormValidation<TValues extends object>(
  formik: FormikProps<TValues>,
  fieldErrors: Record<string, string>,
  fieldLabels: Record<string, string> = {},
): IUseWorkflowFormValidationResult<TValues> {
  const {
    setFieldError,
    setFieldTouched,
    validateForm,
    setTouched,
    touched,
    errors,
  } = formik;

  React.useEffect(() => {
    Object.entries(fieldErrors).forEach(([field, message]) => {
      setFieldTouched(field, true, false);
      setFieldError(field, message);
    });
  }, [fieldErrors, setFieldError, setFieldTouched]);

  const passesValidation = async (): Promise<boolean> => {
    const validationErrors = await validateForm();
    const invalidFields = Object.keys(validationErrors);
    if (invalidFields.length === 0) return true;

    await setTouched(
      invalidFields.reduce<Record<string, boolean>>(
        (acc, field) => ({ ...acc, [field]: true }),
        {},
      ) as FormikTouched<TValues>,
      false,
    );

    const labels = invalidFields.map((field) => fieldLabels[field] ?? field);
    showErrorToast(`Please fix: ${labels.join(', ')}`);
    return false;
  };

  const fieldError = (field: keyof TValues) =>
    touched[field] ? (errors[field] as string | undefined) : undefined;

  return { passesValidation, fieldError };
}
